import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PanelContextValue {
  openPanel: (id: string) => void;
  closePanel: (id: string) => void;
  openPanelId: string | null;
}

interface PanelProviderProps {
  children: ReactNode;
}

const PanelContext = createContext<PanelContextValue | null>(null);

/*
 * Tracks the single open panel in a given context. Only one panel is shown at a
 * time, so opening a panel replaces whichever one was already open.
 */
export const PanelProvider = ({ children }: PanelProviderProps) => {
  const [openPanelId, setOpenPanelId] = useState<string | null>(null);

  const openPanel = useCallback((id: string) => {
    setOpenPanelId(id);
  }, []);

  const closePanel = useCallback((id: string) => {
    setOpenPanelId((current) => (current === id ? null : current));
  }, []);

  const value = useMemo(
    () => ({ openPanel, closePanel, openPanelId }),
    [openPanel, closePanel, openPanelId],
  );

  return (
    <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
  );
};

export const usePanelContext = () => {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanelContext must be used within PanelProvider');
  }
  return context;
};

export const useShouldPanelBeVisible = (panelId: string) => {
  const { openPanelId } = usePanelContext();
  return openPanelId === panelId;
};
